import { useQuery } from "@tanstack/react-query";
import { 
  IconTrendingUp, 
  IconBuildingSkyscraper, 
  IconTargetArrow,
  IconChartPie
} from "@tabler/icons-react";
import { getDashboardSummary } from "../api";
import { KpiCard } from "@/components/data/KpiCard";

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["agency-dashboard-summary"],
    queryFn: getDashboardSummary,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-gray-200" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-600">
        <h3 className="font-bold">Erro ao carregar dashboard</h3>
        <p className="text-sm">{(error as Error)?.message || "Erro desconhecido"}</p>
      </div>
    );
  }

  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold tracking-tight">Resumo de Produção</h2>
        <p className="text-gray-500">
          Acompanhe o desempenho geral da sua imobiliária nos últimos 30 dias.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="VGV Total"
          value={formatter.format(data.totalVgv)}
          icon={<IconTrendingUp size={24} />}
          tint="success"
          description="Venda Geral de Vendas"
        />
        <KpiCard
          label="VGL Total"
          value={formatter.format(data.totalVgl)}
          icon={<IconBuildingSkyscraper size={24} />}
          tint="info"
          description="Valor Geral de Locação"
        />
        <KpiCard
          label="Negócios Ativos"
          value={data.activeDealsCount}
          icon={<IconTargetArrow size={24} />}
          tint="brand"
          description="Em andamento"
        />
        <KpiCard
          label="Conversão"
          value={`${data.conversionRate}%`}
          icon={<IconChartPie size={24} />}
          tint="warning"
          description="Leads para Contrato"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/40 bg-white/70 p-8 shadow-card backdrop-blur-sm">
          <h3 className="mb-6 text-lg font-bold text-ink">Desempenho por Período</h3>
          <div className="flex h-64 items-center justify-center rounded-2xl bg-gray-50/50 border-2 border-dashed border-border transition-colors hover:border-brand-300">
            <span className="text-ink-soft font-bold text-sm uppercase tracking-widest">Gráfico de Produção</span>
          </div>
        </div>
        <div className="rounded-3xl border border-white/40 bg-white/70 p-8 shadow-card backdrop-blur-sm">
          <h3 className="mb-6 text-lg font-bold text-ink">Top Corretores</h3>
          <div className="flex h-64 items-center justify-center rounded-2xl bg-gray-50/50 border-2 border-dashed border-border transition-colors hover:border-brand-300">
            <span className="text-ink-soft font-bold text-sm uppercase tracking-widest">Ranking de Produção</span>
          </div>
        </div>
      </div>
    </div>
  );
}
